import React from 'react';
import { FileCheck, Mail, FileText, UserCheck } from 'lucide-react';
import HelpIconButton from '../HelpIconButton';

// Three-pathway explainer for audit report requests. Each pathway is a
// card with description + SLA + prerequisite + contact info. Placeholder
// contact info until Ryan's handoff doc lands.

interface Pathway {
  label: string;
  description: string;
  sla: string;
  prerequisite: string;
  contact: string;
  icon: React.ElementType;
  accentClass: string;
}

const PATHWAYS: Pathway[] = [
  {
    label: 'Self-Serve',
    description:
      'Public-facing audit summaries and attestations available without a request. Best for initial screening and high-level reviews.',
    sla: 'Immediate',
    prerequisite: 'None',
    contact: 'See the Quick Reference screen for direct links.',
    icon: FileText,
    accentClass: 'border-blue-200 bg-blue-50',
  },
  {
    label: 'NDA Required',
    description:
      'Full audit reports and detailed control narratives available under a mutual NDA. Suitable for third-party risk management reviews.',
    sla: '5 business days',
    prerequisite: 'Executed mutual NDA on file',
    contact: 'trust@example.com',
    icon: UserCheck,
    accentClass: 'border-amber-200 bg-amber-50',
  },
  {
    label: 'White-Glove',
    description:
      'Live walkthrough with a TSG security engineer, direct introductions to sub-provider security teams, and bespoke evidence packages. For regulated-industry reviews.',
    sla: '2-3 weeks (scheduled)',
    prerequisite: 'Executive sponsor on both sides',
    contact: 'trust@example.com (subject: White-Glove Request)',
    icon: Mail,
    accentClass: 'border-emerald-200 bg-emerald-50',
  },
];

const RequestingFullAuditReports: React.FC = () => {
  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-rose-100 rounded-lg">
            <FileCheck className="w-6 h-6 text-rose-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Requesting Full Audit Reports</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Three pathways for obtaining full audit reports from TSG or its sub-providers.
            </p>
          </div>
        </div>
        <HelpIconButton screenId="requesting-full-audit-reports" showLabel />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {PATHWAYS.map(pathway => {
          const Icon = pathway.icon;
          return (
            <div
              key={pathway.label}
              className={`rounded-xl border-2 ${pathway.accentClass} p-5 flex flex-col`}
            >
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-white rounded-lg border border-gray-200">
                  <Icon className="w-5 h-5 text-gray-700" />
                </div>
                <h3 className="text-base font-bold text-gray-900">{pathway.label}</h3>
              </div>
              <p className="mt-3 text-sm text-gray-700 leading-relaxed">{pathway.description}</p>
              <dl className="mt-4 space-y-1.5 text-xs text-gray-600">
                <div className="flex items-baseline gap-2">
                  <dt className="font-semibold w-24 shrink-0">SLA</dt>
                  <dd>{pathway.sla}</dd>
                </div>
                <div className="flex items-baseline gap-2">
                  <dt className="font-semibold w-24 shrink-0">Prerequisite</dt>
                  <dd>{pathway.prerequisite}</dd>
                </div>
                <div className="flex items-baseline gap-2">
                  <dt className="font-semibold w-24 shrink-0">Contact</dt>
                  <dd className="break-all">{pathway.contact}</dd>
                </div>
              </dl>
            </div>
          );
        })}
      </div>

      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-500">
        Placeholder pathway definitions and contact addresses — will be replaced with
        approved text when Ryan&apos;s handoff doc lands.
      </div>
    </div>
  );
};

export default RequestingFullAuditReports;
